import { Vec3, vec3, add, cross, dot, scale, normalize } from '../core/math/vec3'
import { quatFromBasis, rotate, unrotate } from '../core/math/quat'
import { CelestialBody } from '../core/world/body'
import { Vessel } from '../core/vessel/vessel'
import { PartCatalog } from '../core/vessel/catalog'
import { computeMassProperties } from '../core/vessel/massProperties'
import { vesselExtents } from '../core/vessel/extents'
import { RigidState } from '../core/dynamics/rigidBody'
import { atmosphereVelocity } from '../core/dynamics/flight'
import { localUp, localNorth, localEast } from '../core/nav/attitude'

export interface LaunchSite {
  readonly name: string
  readonly bodyId: string
  /** latitudine in radianti, positiva verso nord */
  readonly latitude: number
  /** longitudine in radianti */
  readonly longitude: number
  /** azimut di lancio in radianti: 0 = nord, π/2 = est */
  readonly azimuth: number
}

export const CAPO_GAIA: LaunchSite = {
  name: 'Capo Gaia',
  bodyId: 'gaia',
  latitude: 0,
  longitude: 0,
  // verso est: la rotazione del pianeta regala 175 m/s
  azimuth: Math.PI / 2,
}

/** Punto sulla superficie, o a quota data. Polo nord = +Z. */
export function surfacePosition(
  body: CelestialBody, latitude: number, longitude: number, altitude: number,
): Vec3 {
  const r = body.radius + altitude
  const cl = Math.cos(latitude)
  return vec3(r * cl * Math.cos(longitude), r * cl * Math.sin(longitude), r * Math.sin(latitude))
}

/**
 * Stato iniziale di un veicolo appoggiato sulla rampa.
 *
 * Tre cose facili da sbagliare, tutte sorvegliate da un test:
 * il razzo appoggia invece di affondare o fluttuare; il rollio e definito
 * (il "sopra" del veicolo guarda a nord, altrimenti la navball parte storta);
 * e il veicolo si muove GIA col pianeta, velocita angolare compresa. Quei
 * 175 m/s all'equatore sono regalati, ed e il motivo per cui si lancia a est.
 */
export function launchState(
  body: CelestialBody, site: LaunchSite, vessel: Vessel, catalog: PartCatalog,
): RigidState {
  const mp = computeMassProperties(vessel, catalog)
  const ext = vesselExtents(vessel, catalog)

  const perno = surfacePosition(body, site.latitude, site.longitude, 0)
  const up = localUp(perno)
  const north = localNorth(perno)
  const east = localEast(perno)

  // ROLL PROGRAM. Il muso va allo zenit, ma il rollio non e libero: il dorso
  // del veicolo (+Z) si mette OPPOSTO all'azimut di lancio, cosi il piano di
  // beccheggio coincide col piano del giro di gravita. Senza questo, il razzo
  // beccheggia "di traverso" rispetto al proprio dorso e la navball mostra
  // l'orizzonte verticale — che e esattamente il motivo per cui i lanciatori
  // veri fanno un roll program subito dopo il decollo.
  const azimut = add(scale(north, Math.cos(site.azimuth)), scale(east, Math.sin(site.azimuth)))
  const dorso = scale(normalize(azimut), -1)
  const orientation = quatFromBasis(cross(up, dorso), up, dorso)

  // il centro di massa va tanto in alto quanto serve perche il punto piu
  // basso del razzo tocchi terra
  const bassoLocale = vec3(-mp.com.x, ext.minY - mp.com.y, -mp.com.z)
  const quotaCom = -dot(rotate(orientation, bassoLocale), up)
  const position = surfacePosition(body, site.latitude, site.longitude, quotaCom)

  const omega = body.rotationPeriod > 0
    ? vec3(0, 0, (2 * Math.PI) / body.rotationPeriod)
    : vec3(0, 0, 0)

  return {
    position,
    velocity: atmosphereVelocity(position, body),
    orientation,
    angularVelocity: unrotate(orientation, omega),
  }
}
